import moment from "moment";
import { Typography } from "@material-ui/core";

const CARRIER_ALL = (isAdminRol) => ({
	key: "carrier",
	type: "autocomplete",
	label: "Carrier",
	required: true,
	disabled: true,
	field: {
		classes: {},
		variant: undefined,
	},
	autocomplete: {
		provider: "smarthop",
		url: "api/profile/carriers",
		preloadDefault: true,
		listenEvent: "profileRevision",
		params: { options_active_only: true, options_my_assigned: !isAdminRol },
	},
});

const TRUCK_ASSIGNED = ({ isAdminRol, isDispatcher, isCarrierRol = false, carrierId, onlyShowDrafts }) => ({
	key: "truck",
	type: "autocomplete",
	label: "Truck",
	required: true,
	disabled: true,
	field: {
		classes: {},
		variant: undefined,
	},
	autocomplete: {
		provider: "smarthop",
		preloadDefault: true,
		listenEvent: "profileRevision",
		url: "api/profile/trucks",
		params: {
			options_metadata_truck: true,
			options_metadata_driver: true,
			options_active_only: true,
			label_format_show_driver: true,
			...(isDispatcher ? { options_my_assigned: true } : {}), // Admin can see all trucks
			...(isCarrierRol ? { options_carrier_id_search: carrierId } : {}),
			...(onlyShowDrafts ? { filters: { status_truck_plan: "NO_PUBLISHED" } } : {}),
		},
	},
	...(isAdminRol || isDispatcher
		? {
				rules: {
					params: {
						valueRef: "carrier",
						paramKey: "options_carrier_id_search",
					},
				},
		  }
		: {}),
});

const CURRENT_LOCATION = {
	key: "current_location",
	type: "autocomplete",
	label: "Current Location",
	required: true,
	autocomplete: {
		provider: "trimblemaps",
		params: {
			componentRestrictions: { country: "us", includeOnly: "city" },
			types: ["(cities)"],
		},
		icon: "location",
	},
};

const START_LOCATION = {
	key: "start_location",
	type: "autocomplete",
	label: "Start Location",
	required: true,
	autocomplete: {
		provider: "trimblemaps",
		params: {
			componentRestrictions: { country: "us", includeOnly: "city" },
			types: ["(cities)"],
		},
		icon: "location",
	},
};

const AVAILABLE_DATE = {
	key: "available_date",
	type: "picker",
	label: "Available Date",

	required: true,
	field: {
		classes: {},
		variant: undefined,
	},
	input: {
		classes: {},
	},
	picker: {
		disablePast: false,
		notClearable: true,
		type: "date",
	},
};

const END_LOCATION = {
	key: "end_location",
	type: "autocomplete",
	label: "End Location",

	autocomplete: {
		provider: "trimblemaps",
		params: {
			componentRestrictions: { country: "us", includeOnly: "city" },
			types: ["(cities)"],
		},
		icon: "location",
	},
};

const START_DATE = {
	key: "start_date",
	type: "picker",
	label: "Start Date",
	required: false,
	field: {
		classes: {},
		variant: undefined,
	},
	input: {
		classes: {},
	},
	picker: {
		disablePast: false,
		type: "date",
	},
};
const END_DATE = {
	key: "end_date",
	type: "picker",
	label: "End Date",
	required: false,
	field: {
		classes: {},
		variant: undefined,
	},
	input: {
		classes: {},
	},
	picker: {
		disablePast: true,
		type: "date",
	},
};

const toggleGroup = (name, items, defaultVisible, alwaysVisible = false, icon = false, summary) => {
	let toggle = { defaultVisible };
	if (icon) toggle.icon = icon;
	let config = {
		type: "toggle",
		key: name,
		toggle,
		label: name,
		alwaysVisible,
		content: {
			items,
		},
	};

	if (summary) {
		config.summary = summary;
	}

	return config;
};

const group = (items) => {
	return {
		type: "group",
		content: {
			items,
		},
	};
};

const STRATEGY_STATUS = {
	key: "status",
	type: "select",
	label: "Status",
	required: true,

	options: [
		{ value: "PROPOSING", label: "Proposing" },
		{ value: "ACTIVE", label: "Active" },
		{ value: "IN_PROGRESS", label: "In Progress" },
		{ value: "RECALCULATED", label: "Recalculated" },
		{ value: "COMPLETED", label: "Completed" },
		{ value: "EARLY_FINISH", label: "Early Finish" },
		{ value: "REJECTED", label: "Rejected" },
		{ value: "CANCELED", label: "Canceled" },
	],
};

const STRATEGY_NOTES = {
	key: "notes",
	type: "text",
	label: "Notes",
	multiline: { rows: 4 },
	field: {
		placeholder: "Enter notes about this strategy",
		rows: 4,
	},
};

const GUARANTEE_TOTAL_PAYMENT = {
	key: "guarantee-total_payment",
	type: "number",
	label: "Guaranteed Payment ($)",
	field: {
		placeholder: "Enter guaranteed payment amount",
	},
};

const GUARANTEE_LOADED_MILES = {
	key: "guarantee-total_loaded_miles",
	type: "number",
	label: "Guaranteed Loaded Miles",
	field: {
		placeholder: "Enter guaranteed loaded miles",
	},
};

const GUARANTEE_EMPTY_MILES = {
	key: "guarantee-total_empty_miles",
	type: "number",
	label: "Guaranteed Empty Miles",
	field: {
		placeholder: "Enter guaranteed empty miles",
	},
};

const GUARANTEE_RPM = {
	key: "guarantee-total_rpm",
	type: "number",
	label: "Guaranteed Rate Per Mile ($)",
	field: {
		placeholder: "Enter guaranteed rate per mile",
	},
};

const GUARANTEE_DURATION = {
	key: "guarantee-total_duration",
	type: "number",
	label: "Guaranteed Duration (days)",
	field: {
		placeholder: "Enter guaranteed duration in days",
	},
};

const STRATEGY_INFO_BANNER = {
	key: "strategy_info_banner",
	type: "banner",
	label: "Update strategy status and notes. Changes will be saved in the system history.",
	banner: {
		icon: "info",
		classes: { icon: "text-blue-600" },
	},
};

const GUARANTEE_INFO_BANNER = {
	key: "guarantee_info_banner",
	type: "banner",
	label: "Guarantee information represents the committed values for the strategy.",
	banner: {
		icon: "info",
		classes: { icon: "text-blue-600" },
	},
};

const PROGRESS_INFO = (data) => ({
	key: "progress_info",
	type: "banner",
	label: data?.progress
		? `Current Progress $${data?.progress?.total_payment} / ${
				data?.progress?.total_loaded_miles + data?.progress?.total_empty_miles
		  } mi`
		: "No progress data available",
	banner: {
		icon: "directions_car",
		classes: { icon: "text-green-600" },
	},
});

const submit = () => {
	return {
		type: "action",
		action: "SUBMIT",
		label: "Save Changes",
		button: {
			alwaysEnabled: true,
		},
	};
};

const strategyEditForm = ({ ids, data, mergedData }) => {
	// Check if the strategy is in a terminal state (completed or canceled)
	const isTerminalState = data?.status === "COMPLETED" || data?.status === "CANCELED";

	const strategySection = () => {
		return toggleGroup(
			"Contract Status",
			[
				//STRATEGY_INFO_BANNER,
				group([STRATEGY_STATUS]),
				/*group([CARRIER_ALL(true),
					TRUCK_ASSIGNED({ isAdminRol: true, carrierId: data?.carrier }),
				])*/
				STRATEGY_NOTES,
			],
			true,
			false,
			"assignment",
			(data) => {
				return (
					<>
						{data?.status ? (
							<Typography
								className={`inline-block p-2 text-12 ${
									data?.status === "ACTIVE"
										? "text-green-600"
										: data?.status === "COMPLETED" || data?.status === "CANCELED"
										? "text-red-600"
										: "text-blue-600"
								}`}
								component="div"
							>
								{data?.status || ""}
							</Typography>
						) : null}
					</>
				);
			}
		);
	};

	const guaranteeSection = (data) => {
		return toggleGroup(
			"Guarantee Information",
			[
				//GUARANTEE_INFO_BANNER,
				//PROGRESS_INFO(data),
				group([GUARANTEE_TOTAL_PAYMENT, GUARANTEE_RPM]),
				group([GUARANTEE_LOADED_MILES, GUARANTEE_EMPTY_MILES]),
				GUARANTEE_DURATION,
			],
			false,
			false,
			"verified",
			(data) => {
				const hasGuarantee = true;
				return (
					<Typography
						className={`inline-block p-2 text-12 ${hasGuarantee ? "text-green-600" : "text-grey-600"}`}
						component="div"
					>
						{hasGuarantee
							? `RPM ${data?.["guarantee-total_rpm"] || 0} -> $ ${data?.["guarantee-total_payment"] || 0} / ${
									data?.["guarantee-total_loaded_miles"] + data?.["guarantee-total_empty_miles"] || 0
							  } mi - ${data?.["guarantee-total_duration"] || 0} days`
							: "No guarantee set"}
					</Typography>
				);
			}
		);
	};

	const infoSection = () => {
		return toggleGroup(
			"Contract Details",
			[
				group([CARRIER_ALL(true), TRUCK_ASSIGNED({ isAdminRol: true, carrierId: data?.carrier })]),
				group([CURRENT_LOCATION, AVAILABLE_DATE]),
				group([START_LOCATION, END_LOCATION]),
				group([START_DATE, END_DATE]),
			],
			false,
			false,
			"info",
			(data) => {
				if (data) {
					return (
						<Typography className="inline-block p-2 text-green text-12" component="div">
							{moment(data?.available_date).format("MMM D")} @ {data?.current_location}
						</Typography>
					);
				} else {
					return null;
				}
			}
		);
	};

	return {
		header: {
			edit: {
				label: "Edit Contract",
			},
		},
		preventClose: {
			enable: true,
		},
		items: [strategySection(), guaranteeSection(data), infoSection(), submit()],
	};
};

export { strategyEditForm };
